import { DecisionType } from "../../common/decisions";
import {
  ArticlesOfAssociationChangeMode,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  extractSectionWithMaxScoreFromArticles,
  updateArticlesModeModel,
} from "../common/commonDomain";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { makeAutoObservable } from "mobx";
import { newUuid } from "../../utils/uuid";
import { ChangeArticlesOfAssociationModel } from "../changeArticlesOfAssociation/changeArticlesOfAssociationDomain";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "../../common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../../common/share";

export type ConversionOfShares = {
  type: DecisionType.conversionOfShares;
  fromShareTypeId?: number;
  toShareTypeId?: number;
  toNewShareTypeCode?: ShareTypeCode;
  convertAllShares: boolean;
  sharesPerShareholder: ConvertSharesPerShareholder[];
  updateArticlesAutomatically?: boolean;
  articleOfAssociationNumber?: number;
  articleChanges: ArticlesOfAssociationChangeMode[];
};

export type ConvertSharesPerShareholder = {
  shareholderId: number;
  shareholderName: string;
  numberOfShares: number;
};

export class ConversionOfSharesForm {
  readonly type = DecisionType.conversionOfShares;
  fromShareTypeModel: RadioFieldModel<ShareType>;
  toShareTypeModel: RadioFieldModel<ShareType | null>;
  articleOfAssociationNumber: ArticleSelectorModel;
  convertAllShares: BooleanFieldModel;
  sharesPerShareholder: ConvertSharesPerShareholderModel[];
  showAddShareClassDialog: boolean = false;
  createShareTypeModel: RadioFieldModel<ShareTypeCode>;
  fromShareTypes: ShareType[];
  toShareTypes: ShareType[];
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  articleChangeModel: ChangeArticlesOfAssociationModel;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext,
    readonly data?: ConversionOfShares,
  ) {
    this.fromShareTypes = context.companyInfo.shareTypes.filter(
      (st) =>
        !st.historical &&
        context.inputState?.numberOfShares.some(
          (s) => s.shareTypeCode === st.sharetypeCode && s.numberOfShares > 0,
        ),
    );
    this.toShareTypes = context.companyInfo.shareTypes.filter((st) =>
      context.inputState?.numberOfShares.some((s) => s.shareTypeCode === st.sharetypeCode),
    );

    this.fromShareTypeModel = new RadioFieldModel({
      value:
        this.fromShareTypes.find((shareType) => shareType.sharetypeId === data?.fromShareTypeId) ??
        this.fromShareTypes[0],
      options: this.fromShareTypes,
      labelFn: (shareType) => (shareType ? shareTypeLabel(shareType) : { translated: "" }),
      idFn: (shareType) => String(shareType?.sharetypeId),
      onChange: () => (this.toShareTypeModel = this.createToShareModel()),
    });

    if (data?.toNewShareTypeCode) {
      this.toShareTypes.push({
        sharetypeId: 0,
        organizationnumber: this.context.orgNo,
        name: data.toNewShareTypeCode,
        vote: 1,
        sharetypeCode: data.toNewShareTypeCode,
        historical: false,
      });
    }
    this.toShareTypeModel = this.createToShareModel(
      this.toShareTypes.find((st) => st.sharetypeId === data?.toShareTypeId),
    );

    this.articleOfAssociationNumber = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );
    this.convertAllShares = new BooleanFieldModel({ value: data?.convertAllShares });

    this.sharesPerShareholder = this.context.shareholderData.shareholders.map(
      (s) =>
        new ConvertSharesPerShareholderModel(
          s,
          data?.sharesPerShareholder?.find((d) => d.shareholderId === s.id)?.numberOfShares,
        ),
    );

    const shareTypeCodes = Object.values(ShareTypeCode).filter(
      (s) => !this.toShareTypes.some((st) => st.sharetypeCode === s) && s !== ShareTypeCode.UNKNOWN,
    );
    this.createShareTypeModel = new RadioFieldModel({
      value: shareTypeCodes[0],
      options: shareTypeCodes,
      labelFn: (shareTypeCode) => shareTypeLabel(shareTypeCode),
      idFn: (shareTypeCode) => shareTypeCode,
    });

    this.updateArticlesAutomatically = updateArticlesModeModel(data?.updateArticlesAutomatically);

    this.articleChangeModel = new ChangeArticlesOfAssociationModel(
      this.context,
      data?.articleChanges,
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }

  private createToShareModel(value?: ShareType) {
    return new RadioFieldModel({
      value: value ?? null,
      options: this.toShareTypes.filter(
        (shareType) => shareType.sharetypeId !== this.fromShareTypeModel.value.sharetypeId,
      ),
      labelFn: (shareType) => (shareType ? shareTypeLabel(shareType) : { translated: "" }),
      idFn: (shareType) => String(shareType?.sharetypeId),
      required: true,
    });
  }

  validate() {
    this.fromShareTypeModel.validate();
    this.toShareTypeModel.validate();
    this.updateArticlesAutomatically.validate();
    this.articleChangeModel.validate();
    if (this.fromShareTypeModel.value === this.toShareTypeModel.value) {
      this.toShareTypeModel.error = { key: "decisions.conversionOfShares.errorSameShareClass" };
    }
    this.sharesPerShareholder.forEach((s) => s.validate(this.fromShareTypeModel.value));
    return this.isValid;
  }

  get isUpdateArticlesAutomatically() {
    return this.updateArticlesAutomatically.value === true;
  }

  get isUpdateArticlesManually() {
    return this.updateArticlesAutomatically.value === false;
  }

  get isArticleChangesValid(): boolean {
    const isAutomaticArticleChangesValid =
      this.isUpdateArticlesAutomatically && this.articleOfAssociationNumber.isValid;
    const isManualArticleChangesValid =
      this.isUpdateArticlesManually && this.articleChangeModel.isValid;
    return isAutomaticArticleChangesValid || isManualArticleChangesValid;
  }

  get isValid(): boolean {
    const fromValue = this.fromShareTypeModel.value;
    const toValue = this.toShareTypeModel.value;
    return (
      this.fromShareTypeModel.isValid &&
      this.toShareTypeModel.isValid &&
      fromValue?.sharetypeId !== toValue?.sharetypeId &&
      this.sharesPerShareholder.every((s) => s.isValid) &&
      this.updateArticlesAutomatically.isValid &&
      this.isArticleChangesValid
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  init() {
    if (this.data === undefined || this.data.articleOfAssociationNumber === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success": {
        if (this.articleOfAssociationNumber.selected === undefined) {
          this.articleOfAssociationNumber.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
      }
    }
  }

  setShowAddShareClassDialog(value: boolean) {
    this.showAddShareClassDialog = value;
  }

  addNewShareType() {
    const newShareType = {
      sharetypeId: 0,
      organizationnumber: this.context.orgNo,
      name: this.createShareTypeModel.value,
      vote: 1,
      sharetypeCode: this.createShareTypeModel.value,
      historical: false,
    };
    this.toShareTypes.push(newShareType);
    this.toShareTypeModel = this.createToShareModel(newShareType);
    this.showAddShareClassDialog = false;
  }
}

export class ConvertSharesPerShareholderModel {
  id = newUuid();
  shareholder: ExtendedShareholderDTO;
  numberOfShares: NumberFieldModel;

  constructor(shareholder: ExtendedShareholderDTO, numberOfShares?: number) {
    this.shareholder = shareholder;
    this.numberOfShares = new NumberFieldModel({
      isInteger: true,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
  }

  validate(shareType: ShareType) {
    this.numberOfShares.validate();

    const parsedValue = this.numberOfShares.parsedValue;
    const shareholderShares = this.shareholder.calculatedShares[shareType.sharetypeCode];
    if (parsedValue) {
      if (shareholderShares) {
        if (parsedValue > shareholderShares) {
          this.numberOfShares.error = {
            key: "transactions.conversionOfShares.numberExceededError",
          };
        }
      } else {
        this.numberOfShares.error = {
          key: "transactions.conversionOfShares.numberExceededError",
        };
      }
    }
  }

  get isValid() {
    return this.numberOfShares.isValid;
  }
}
